import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Pencil, Search, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { pickLocalized } from '@/hooks/useLandingContent';

type StoryRow = {
  id: string;
  property_id: string | null;
  title: any;
  cover_url: string | null;
  link_url: string | null;
  display_order: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  properties?: {
    id: string;
    reference_code: string | null;
    title: any;
    price: number | null;
    currency: string | null;
    city: string | null;
    property_images?: { url: string; is_primary: boolean }[];
  } | null;
};

type PropertyOption = {
  id: string;
  reference_code: string | null;
  title: any;
  price: number | null;
  currency: string | null;
  city: string | null;
  property_images?: { url: string; is_primary: boolean }[];
};

export default function StoriesManager() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<StoryRow | null>(null);

  const { data: stories, isLoading } = useQuery({
    queryKey: ['admin-stories'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('stories')
        .select(`
          id, property_id, title, cover_url, link_url, display_order, is_active,
          starts_at, ends_at, created_at,
          properties:property_id (
            id, reference_code, title, price, currency, city,
            property_images(url, is_primary)
          )
        `)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as StoryRow[];
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin-stories'] });
    qc.invalidateQueries({ queryKey: ['landing-stories-curated'] });
  };

  const toggleActive = async (s: StoryRow) => {
    const { error } = await (supabase as any).from('stories').update({ is_active: !s.is_active }).eq('id', s.id);
    if (error) toast.error(error.message);
    else { toast.success(!s.is_active ? 'Story activated' : 'Story deactivated'); refresh(); }
  };

  const moveStory = async (s: StoryRow, dir: -1 | 1) => {
    if (!stories) return;
    const idx = stories.findIndex(x => x.id === s.id);
    const swap = stories[idx + dir];
    if (!swap) return;
    const updates = [
      (supabase as any).from('stories').update({ display_order: swap.display_order }).eq('id', s.id),
      (supabase as any).from('stories').update({ display_order: s.display_order }).eq('id', swap.id),
    ];
    await Promise.all(updates);
    refresh();
  };

  const deleteStory = async (s: StoryRow) => {
    if (!confirm('Delete this story?')) return;
    const { error } = await (supabase as any).from('stories').delete().eq('id', s.id);
    if (error) toast.error(error.message);
    else { toast.success('Story deleted'); refresh(); }
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Stories CMS</h1>
          <p className="text-sm text-muted-foreground">Curate Stories shown on the homepage. Each story is linked to an existing approved property.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Create Story
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>All Stories</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-6 w-6 inline-block animate-spin" /></div>
          ) : !stories || stories.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">No stories yet. Create one to get started.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Cover</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Price / City</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stories.map((s, i) => {
                  const p = s.properties;
                  const cover = s.cover_url || p?.property_images?.find(x => x.is_primary)?.url || p?.property_images?.[0]?.url;
                  return (
                    <TableRow key={s.id}>
                      <TableCell className="w-24">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium w-6">{s.display_order}</span>
                          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => moveStory(s, -1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === stories.length - 1} onClick={() => moveStory(s, 1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="h-12 w-16 rounded overflow-hidden bg-muted">
                          {cover && <img src={cover} alt="" className="w-full h-full object-cover" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{pickLocalized(s.title || p?.title, 'en') || '—'}</div>
                        <div className="text-xs text-muted-foreground">{p?.reference_code || '—'}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{p?.currency || 'ETB'} {p?.price?.toLocaleString() || '—'}</div>
                        <div className="text-xs text-muted-foreground">{p?.city || ''}</div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {s.starts_at && <div>From {new Date(s.starts_at).toLocaleDateString()}</div>}
                        {s.ends_at && <div>To {new Date(s.ends_at).toLocaleDateString()}</div>}
                        {!s.starts_at && !s.ends_at && <span>Always</span>}
                      </TableCell>
                      <TableCell><Switch checked={s.is_active} onCheckedChange={() => toggleActive(s)} /></TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(s)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteStory(s)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <StoryFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        existing={null}
        onSaved={refresh}
        nextOrder={(stories?.[stories.length - 1]?.display_order ?? 0) + 1}
      />
      <StoryFormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        existing={editing}
        onSaved={refresh}
      />
    </div>
  );
}

// ─── Form Dialog ───────────────────────────────────────────────────────

function StoryFormDialog({
  open, onOpenChange, existing, onSaved, nextOrder,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  existing: StoryRow | null;
  onSaved: () => void;
  nextOrder?: number;
}) {
  const isEdit = !!existing;
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [propertyMeta, setPropertyMeta] = useState<PropertyOption | null>(null);
  const [titleEn, setTitleEn] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [order, setOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setPropertyId(existing.property_id);
      setPropertyMeta(existing.properties || null);
      setTitleEn((existing.title as any)?.en || '');
      setCoverUrl(existing.cover_url || '');
      setLinkUrl(existing.link_url || '');
      setIsActive(existing.is_active);
      setStartsAt(existing.starts_at ? existing.starts_at.slice(0, 16) : '');
      setEndsAt(existing.ends_at ? existing.ends_at.slice(0, 16) : '');
      setOrder(existing.display_order);
    } else {
      setPropertyId(null); setPropertyMeta(null);
      setTitleEn(''); setCoverUrl(''); setLinkUrl('');
      setIsActive(true); setStartsAt(''); setEndsAt('');
      setOrder(nextOrder ?? 0);
    }
  }, [open, existing, nextOrder]);

  const handleSave = async () => {
    if (!propertyId) { toast.error('Pick a property first'); return; }
    setSaving(true);
    const titleJson = { en: titleEn || (propertyMeta?.title as any)?.en || '' };
    const payload: any = {
      property_id: propertyId,
      title: titleJson,
      cover_url: coverUrl || null,
      link_url: linkUrl || null,
      is_active: isActive,
      display_order: order,
      starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
    };
    let error;
    if (isEdit && existing) {
      ({ error } = await (supabase as any).from('stories').update(payload).eq('id', existing.id));
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      payload.created_by = user?.id;
      ({ error } = await (supabase as any).from('stories').insert(payload));
    }
    setSaving(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(isEdit ? 'Story updated' : 'Story created');
      onSaved();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Story' : 'Create Story'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-1 block">Linked Property *</Label>
            {isEdit ? (
              <div className="border rounded-md p-3 bg-muted/30 text-sm">
                <div className="font-medium">{pickLocalized(propertyMeta?.title, 'en') || '—'}</div>
                <div className="text-xs text-muted-foreground">{propertyMeta?.reference_code} · {propertyMeta?.city}</div>
              </div>
            ) : (
              <PropertyPicker
                onPick={(p) => {
                  setPropertyId(p.id);
                  setPropertyMeta(p);
                  if (!titleEn) setTitleEn((p.title as any)?.en || '');
                }}
                selected={propertyMeta}
              />
            )}
          </div>

          <div>
            <Label htmlFor="title-en">Title (override, optional)</Label>
            <Input id="title-en" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} placeholder="Defaults to property title" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cover">Cover image URL (optional)</Label>
              <Input id="cover" value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} placeholder="Defaults to primary property image" />
            </div>
            <div>
              <Label htmlFor="link">Custom link (optional)</Label>
              <Input id="link" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Defaults to property page" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="order">Display order</Label>
              <Input id="order" type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} />
            </div>
            <div>
              <Label htmlFor="starts">Starts at (optional)</Label>
              <Input id="starts" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ends">Ends at (optional)</Label>
              <Input id="ends" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch id="active" checked={isActive} onCheckedChange={setIsActive} />
            <Label htmlFor="active">Active (visible on landing page)</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !propertyId}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Story'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Property Picker ───────────────────────────────────────────────────

function PropertyPicker({ onPick, selected }: { onPick: (p: PropertyOption) => void; selected: PropertyOption | null }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 250); return () => clearTimeout(t); }, [q]);

  const { data: results, isLoading } = useQuery({
    queryKey: ['admin-stories-property-picker', debounced],
    queryFn: async () => {
      let query = (supabase as any)
        .from('properties')
        .select('id, reference_code, title, price, currency, city, property_images(url, is_primary)')
        .eq('status', 'active')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false })
        .limit(15);
      if (debounced.trim()) {
        query = query.or(`reference_code.ilike.%${debounced}%,city.ilike.%${debounced}%,title->>en.ilike.%${debounced}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as PropertyOption[];
    },
  });

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by reference, title, or city..." className="pl-8" />
      </div>
      {selected && (
        <div className="border rounded-md p-2 bg-primary/5 flex items-center gap-3">
          <img src={selected.property_images?.find(i => i.is_primary)?.url || selected.property_images?.[0]?.url} alt="" className="h-12 w-16 object-cover rounded" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{pickLocalized(selected.title, 'en')}</div>
            <div className="text-xs text-muted-foreground">{selected.reference_code} · {selected.city} · {selected.currency} {selected.price?.toLocaleString()}</div>
          </div>
          <Badge>Selected</Badge>
        </div>
      )}
      <div className="border rounded-md max-h-64 overflow-y-auto divide-y">
        {isLoading && <div className="p-3 text-xs text-muted-foreground">Searching…</div>}
        {!isLoading && results?.length === 0 && <div className="p-3 text-xs text-muted-foreground">No properties found</div>}
        {results?.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p)}
            className="w-full flex items-center gap-3 p-2 hover:bg-muted/50 text-left"
          >
            <img src={p.property_images?.find(i => i.is_primary)?.url || p.property_images?.[0]?.url} alt="" className="h-10 w-14 object-cover rounded bg-muted" />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{pickLocalized(p.title, 'en')}</div>
              <div className="text-xs text-muted-foreground">{p.reference_code} · {p.city} · {p.currency} {p.price?.toLocaleString()}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
